import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { contactListsKeys } from '#contactListFeature/models/keys';
import { useContactLists } from '#contactListFeature/models/queries';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';

import { companiesKeys } from '../../models/companies/keys';
import { contactsKeys } from '../../models/contacts/keys';
import { useAnalyzeCrmImport, useStartCrmImport } from '../../models/imports/mutations';
import { useImportJobPolling } from '../../models/imports/queries';
import { usePipelines } from '../../models/pipelines/queries';
import { tagsKeys } from '../../models/tags/keys';
import { useCreateTag } from '../../models/tags/mutations';
import { useTags } from '../../models/tags/queries';

import ColumnMappingStep from './steps/ColumnMappingStep';
import FileUploadStep from './steps/FileUploadStep';
import ImportResultStep from './steps/ImportResultStep';

const STEPS = {
	UPLOAD: 1,
	MAPPING: 2,
	RESULT: 3,
};

const STEP_LABELS = [
	{ step: STEPS.UPLOAD, label: 'Datei' },
	{ step: STEPS.MAPPING, label: 'Zuordnung' },
	{ step: STEPS.RESULT, label: 'Import' },
];

const TERMINAL_STATUSES = ['completed', 'failed'];

/**
 * With `contactList` the wizard imports into that list: the list is fixed and the CRM-only options (pipeline, tags)
 * are left out — the list's CRM sync creates deals and the list tags its companies itself.
 */
export default function CrmImportWizard({ isOpen, onClose, contactList = null }) {
	const initialContactListId = contactList?.id ?? '';
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useError();

	const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
	const [file, setFile] = useState(null);
	const [sampleRows, setSampleRows] = useState([]);
	const [headers, setHeaders] = useState([]);
	const [columnMapping, setColumnMapping] = useState({});
	const [analysisResult, setAnalysisResult] = useState(null);
	const [mode, setMode] = useState('upsert');
	const [fuzzyNameMatch, setFuzzyNameMatch] = useState(false);
	const [pipelineId, setPipelineId] = useState('');
	const [stageId, setStageId] = useState('');
	const [importTags, setImportTags] = useState([]);
	const [contactListId, setContactListId] = useState(initialContactListId);
	const [importResult, setImportResult] = useState(null);
	const [asyncJobId, setAsyncJobId] = useState(null);

	const analyzeMutation = useAnalyzeCrmImport();
	const startMutation = useStartCrmImport();
	const jobQuery = useImportJobPolling(asyncJobId, { enabled: Boolean(asyncJobId) });
	const { data: pipelines = [] } = usePipelines();
	const { data: tagsData } = useTags();
	const allTags = tagsData?.tags ?? [];
	const createTag = useCreateTag();
	const { data: contactLists = [] } = useContactLists();

	const handleCreateTag = useCallback(
		async (name) => {
			const { tag } = await createTag.mutateAsync({ name });
			return tag;
		},
		[createTag],
	);

	const invalidateCrmLists = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: contactsKeys.all });
		void queryClient.invalidateQueries({ queryKey: companiesKeys.all });
		void queryClient.invalidateQueries({ queryKey: tagsKeys.all });
		void queryClient.invalidateQueries({ queryKey: contactListsKeys.all });
	}, [queryClient]);

	// Step 1: file selected → upload + analyze on the backend (server-side parse)
	const handleFileSelected = useCallback(
		async (selectedFile) => {
			setFile(selectedFile);
			try {
				const result = await analyzeMutation.mutateAsync(selectedFile);
				setHeaders(result.headers ?? []);
				setSampleRows(result.sampleRows ?? []);
				setAnalysisResult(result);
				setColumnMapping(result.suggestedMapping ?? {});
				setCurrentStep(STEPS.MAPPING);
			} catch (err) {
				showError({ title: 'Analyse fehlgeschlagen', error: err });
			}
		},
		[analyzeMutation, showError],
	);

	// Step 2: mapping + mode confirmed → upload + start import
	const handleStartImport = useCallback(
		async (mapping) => {
			setColumnMapping(mapping);
			setCurrentStep(STEPS.RESULT);
			try {
				const result = await startMutation.mutateAsync({
					file,
					columnMapping: mapping,
					mode,
					...(pipelineId && stageId ? { pipelineId, stageId } : {}),
					applyTagIds: importTags.map((t) => t.id),
					fuzzyNameMatch,
					contactListId: contactListId || undefined,
				});
				if (result.status === 'completed') {
					setImportResult(result);
					invalidateCrmLists();
					notifySuccess(result.summary, showSuccess);
				} else if (result.status === 'processing') {
					setAsyncJobId(result.jobId);
				}
			} catch (err) {
				showError({ title: 'Import fehlgeschlagen', error: err });
				setImportResult({ status: 'failed', summary: emptySummary(), errors: [] });
			}
		},
		[
			file,
			mode,
			pipelineId,
			stageId,
			importTags,
			fuzzyNameMatch,
			contactListId,
			startMutation,
			showError,
			showSuccess,
			invalidateCrmLists,
		],
	);

	// When an async job reaches a terminal state, persist the result, refresh
	// CRM lists once, and stop polling. A `summaryStale` payload is the 404 "job gone"
	// sentinel — treat it as terminal but don't overwrite a result we already captured.
	const asyncStatus = jobQuery.data?.status;
	useEffect(() => {
		if (asyncJobId && asyncStatus && TERMINAL_STATUSES.includes(asyncStatus)) {
			if (!jobQuery.data?.summaryStale) setImportResult(jobQuery.data);
			invalidateCrmLists();
			setAsyncJobId(null);
		}
	}, [asyncJobId, asyncStatus, jobQuery.data, invalidateCrmLists]);

	const effectiveResult = importResult ?? (asyncJobId ? jobQuery.data : null);

	const handleClose = useCallback(() => {
		setCurrentStep(STEPS.UPLOAD);
		setFile(null);
		setSampleRows([]);
		setHeaders([]);
		setColumnMapping({});
		setAnalysisResult(null);
		setMode('upsert');
		setFuzzyNameMatch(false);
		setPipelineId('');
		setStageId('');
		setImportTags([]);
		setContactListId(initialContactListId);
		setImportResult(null);
		setAsyncJobId(null);
		onClose();
	}, [onClose, initialContactListId]);

	const crmOnlyOptions = {
		pipelines,
		pipelineId,
		stageId,
		onPipelineChange: setPipelineId,
		onStageChange: setStageId,
		allTags,
		importTags,
		onImportTagsChange: setImportTags,
		onCreateTag: handleCreateTag,
		contactLists,
		contactListId,
		onContactListChange: setContactListId,
	};

	const isProcessing = startMutation.isPending || (Boolean(asyncJobId) && jobQuery.data?.status === 'processing');

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header onClose={handleClose}>
				{contactList ? `Import in Liste „${contactList.name}“` : 'Kontakte & Firmen importieren'}
			</Panel.Header>

			<StepIndicator currentStep={currentStep} />

			<Panel.Body className="min-h-[400px]">
				{currentStep === STEPS.UPLOAD && (
					<FileUploadStep
						isAnalyzing={analyzeMutation.isPending}
						onFileSelected={handleFileSelected}
						onCancel={handleClose}
					/>
				)}

				{currentStep === STEPS.MAPPING && analysisResult && (
					<ColumnMappingStep
						{...(contactList ? { listName: contactList.name } : crmOnlyOptions)}
						headers={headers}
						sampleRows={sampleRows}
						availableTargetFields={analysisResult.availableTargetFields}
						columnMapping={columnMapping}
						onMappingChange={setColumnMapping}
						mode={mode}
						onModeChange={setMode}
						fuzzyNameMatch={fuzzyNameMatch}
						onFuzzyNameMatchChange={setFuzzyNameMatch}
						onConfirm={handleStartImport}
						onBack={() => setCurrentStep(STEPS.UPLOAD)}
					/>
				)}

				{currentStep === STEPS.RESULT && (
					<ImportResultStep
						result={effectiveResult}
						isLoading={isProcessing}
						totalRows={jobQuery.data?.totalRows ?? 0}
						onClose={handleClose}
					/>
				)}
			</Panel.Body>
		</Modal>
	);
}

function StepIndicator({ currentStep }) {
	return (
		<div className="flex items-center px-5 py-3.5 border-b border-bgBase-400 flex-shrink-0">
			{STEP_LABELS.map(({ step, label }, index) => (
				<div key={step} className="flex items-center">
					<div
						className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
							currentStep >= step ? 'bg-primary-500 text-white' : 'bg-bgBase-400 text-text'
						}`}
					>
						{index + 1}
					</div>
					<span className={`ml-2 text-sm ${currentStep >= step ? 'text-text' : 'text-bgBase-800'}`}>
						{label}
					</span>
					{index < STEP_LABELS.length - 1 && (
						<div className={`w-12 h-0.5 mx-3 ${currentStep > step ? 'bg-primary-500' : 'bg-bgBase-400'}`} />
					)}
				</div>
			))}
		</div>
	);
}

function emptySummary() {
	return { companiesCreated: 0, companiesUpdated: 0, contactsCreated: 0, contactsUpdated: 0, skipped: 0, failed: 0 };
}

function notifySuccess(summary, showSuccess) {
	const created = (summary?.contactsCreated ?? 0) + (summary?.companiesCreated ?? 0);
	const updated = (summary?.contactsUpdated ?? 0) + (summary?.companiesUpdated ?? 0);
	if (created + updated > 0) {
		showSuccess({
			title: 'Import erfolgreich',
			message: `${created} neu angelegt, ${updated} aktualisiert`,
			autoClose: true,
			autoCloseDuration: 5000,
		});
	}
}

CrmImportWizard.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	contactList: PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired }),
};

StepIndicator.propTypes = {
	currentStep: PropTypes.number.isRequired,
};
